import React, { useState } from 'react';
import { MDText } from 'i18n-react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { SpannerIcon } from '@motorway/mw-highway-code';
import { Toggle } from '@motorway/mw-highway-code/alpha';

import useUser from 'Utilities/hooks/useUser';

import { SHOW_EV_SPECIFICATION_COOKIE } from './devTools.consts';
import { isDevAccount } from './devTools.helpers';
import texts from './DevToolsLauncher.texts';

import styles from './DevToolsLauncher.module.scss';

const localT = new MDText(texts);

const PANEL_ID = 'dev-tools-panel';

const setShowEvSpecificationCookie = (isOn: boolean) => {
	if (isOn) {
		Cookies.set(SHOW_EV_SPECIFICATION_COOKIE, 'true', { path: '/', sameSite: 'lax' });
	} else {
		Cookies.remove(SHOW_EV_SPECIFICATION_COOKIE, { path: '/' });
	}
};

/**
 * Bottom-left launcher, mirroring the Zendesk one at the bottom-right, with
 * feature toggles for testing on dev accounts. Renders nothing for anyone else.
 */
export const DevToolsLauncher: React.FC = () => {
	const user = useUser();
	const router = useRouter();
	const [isOpen, setIsOpen] = useState(false);
	const [isEvSpecificationOn, setIsEvSpecificationOn] = useState(
		() => Cookies.get(SHOW_EV_SPECIFICATION_COOKIE) === 'true',
	);

	if (!isDevAccount(user)) {
		return null;
	}

	const onToggleEvSpecification = () => {
		const isOn = !isEvSpecificationOn;
		setShowEvSpecificationCookie(isOn);
		setIsEvSpecificationOn(isOn);
		// The cookie is read in getServerSideProps, so re-run it for the current page.
		void router.replace(router.asPath, undefined, { scroll: false });
	};

	return (
		<div className={styles.container}>
			{isOpen && (
				<div aria-label={texts.devTools} className={styles.panel} id={PANEL_ID} role="dialog">
					<p className={styles.title}>{localT.translate('devTools')}</p>
					<Toggle
						showLabel
						formProps={{
							input: {
								checked: isEvSpecificationOn,
								onChange: onToggleEvSpecification,
							},
						}}
						id="dev-tools-show-ev-specification"
						label={localT.translate('showEvSpecification')}
					/>
					<p className={styles.hint}>{localT.translate('onlyVisibleToDevAccounts')}</p>
				</div>
			)}
			<button
				aria-controls={PANEL_ID}
				aria-expanded={isOpen}
				aria-label={isOpen ? texts.hideDevTools : texts.showDevTools}
				className={styles.button}
				onClick={() => setIsOpen(!isOpen)}
				type="button"
			>
				<SpannerIcon />
			</button>
		</div>
	);
};
